import React, { useEffect, useState } from 'react';
import {
  getComparaisonStations, getVentesProduits, getEvolutionJournaliere,
  getTopFlop, getEcarts, getSyntheseGroupe, getStations, exportCsv
} from '../services/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const formatMontant = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));
const COLORS = ['#ED0000', '#FF6B35', '#FFC72C', '#78BE20', '#004A97', '#8B5CF6', '#EC4899'];
const MOIS_LABELS = ['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'];

const Reporting: React.FC = () => {
  const [tab, setTab] = useState<'groupe' | 'comparaison' | 'topflop' | 'produits' | 'evolution' | 'ecarts'>('groupe');
  const [comparaison, setComparaison] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [evolution, setEvolution] = useState<any[]>([]);
  const [topFlop, setTopFlop] = useState<any>({ top: [], flop: [] });
  const [ecarts, setEcarts] = useState<any[]>([]);
  const [synthese, setSynthese] = useState<any>(null);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [stationId, setStationId] = useState('');

  useEffect(() => { getStations().then(r => setStations(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadData(); }, [tab, mois, annee, stationId]);

  const loadData = async () => {
    setLoading(true);
    const params: any = { mois: String(mois), annee: String(annee) };
    if (stationId) params.station_id = stationId;
    try {
      if (tab === 'groupe') setSynthese((await getSyntheseGroupe({ annee: String(annee) })).data);
      if (tab === 'comparaison') setComparaison((await getComparaisonStations(params)).data);
      if (tab === 'topflop') setTopFlop((await getTopFlop(params)).data);
      if (tab === 'produits') setProduits((await getVentesProduits(params)).data);
      if (tab === 'evolution') setEvolution((await getEvolutionJournaliere(params)).data);
      if (tab === 'ecarts') setEcarts((await getEcarts(params)).data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleExport = async (type: string) => {
    try {
      const params: any = { type, mois: String(mois), annee: String(annee) };
      if (stationId) params.station_id = stationId;
      const response = await exportCsv(params);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${type}_${mois}_${annee}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Reporting</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={mois} onChange={e => setMois(Number(e.target.value))} className="form-control" style={{ width: 100 }}>
            {MOIS_LABELS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={annee} onChange={e => setAnnee(Number(e.target.value))} className="form-control" style={{ width: 90 }}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {tab !== 'groupe' && tab !== 'comparaison' && tab !== 'topflop' && (
            <select value={stationId} onChange={e => setStationId(e.target.value)} className="form-control" style={{ width: 180 }}>
              <option value="">Toutes les stations</option>
              {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="tabs" style={{ flexWrap: 'wrap' }}>
        {[
          { key: 'groupe', label: 'Vue Groupe' },
          { key: 'comparaison', label: 'Comparaison' },
          { key: 'topflop', label: 'Top / Flop' },
          { key: 'produits', label: 'Ventes Produits' },
          { key: 'evolution', label: 'Evolution' },
          { key: 'ecarts', label: 'Ecarts' },
        ].map(t => (
          <button key={t.key} className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key as any)}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="loading">Chargement...</div> : (
        <>
          {/* VUE GROUPE */}
          {tab === 'groupe' && synthese && (
            <>
              <div className="stats-grid">
                <div className="stat-card"><div className="stat-value">{synthese.totaux.total_stations}</div><div className="stat-label">Total stations</div></div>
                <div className="stat-card" style={{ borderTop: '4px solid #78BE20' }}><div className="stat-value">{synthese.totaux.stations_actives}</div><div className="stat-label">Stations actives</div></div>
                <div className="stat-card" style={{ borderTop: '4px solid #ED0000' }}><div className="stat-value">{formatMontant(synthese.totaux.ca_total)}</div><div className="stat-label">CA Total {annee}</div></div>
                <div className="stat-card" style={{ borderTop: '4px solid #004A97' }}><div className="stat-value">{formatMontant(synthese.totaux.volume_total)}</div><div className="stat-label">Volume Total (L)</div></div>
              </div>

              <div className="card" style={{ marginBottom: 20 }}>
                <h3>Evolution mensuelle du CA - {annee}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={synthese.mensuel.map((m: any) => ({ ...m, moisLabel: MOIS_LABELS[m.mois - 1] }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="moisLabel" />
                    <YAxis tickFormatter={(v: any) => `${Math.round(v / 1000000)}M`} />
                    <Tooltip formatter={(v: any) => formatMontant(Number(v)) + ' FCFA'} />
                    <Area type="monotone" dataKey="ca" name="CA" stroke="#ED0000" fill="#ED000020" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="card">
                <h3>Performance par region</h3>
                <table className="table">
                  <thead>
                    <tr><th>Region</th><th style={{textAlign:'right'}}>Stations</th><th style={{textAlign:'right'}}>Volume</th><th style={{textAlign:'right'}}>CA</th></tr>
                  </thead>
                  <tbody>
                    {synthese.parRegion.map((r: any, i: number) => (
                      <tr key={i}>
                        <td><strong>{r.region || 'Non definie'}</strong></td>
                        <td style={{textAlign:'right'}}>{r.nb_stations}</td>
                        <td style={{textAlign:'right'}}>{formatMontant(r.volume)} L</td>
                        <td style={{textAlign:'right'}}>{formatMontant(r.ca)} FCFA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* COMPARAISON */}
          {tab === 'comparaison' && (
            <>
              <div style={{ marginBottom: 12, textAlign: 'right' }}>
                <button className="btn btn-sm" onClick={() => handleExport('comparaison')}>Exporter CSV</button>
              </div>
              <div className="card" style={{ marginBottom: 20 }}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={comparaison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nom" angle={-45} textAnchor="end" height={100} fontSize={11} />
                    <YAxis tickFormatter={(v: any) => `${Math.round(v / 1000000)}M`} />
                    <Tooltip formatter={(v: any) => formatMontant(v) + ' FCFA'} />
                    <Legend />
                    <Bar dataKey="ca" name="CA realise" fill="#ED0000" />
                    <Bar dataKey="objectif_ca" name="Objectif" fill="#e0e0e0" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="table">
                <thead>
                  <tr><th>Station</th><th>Region</th><th style={{textAlign:'right'}}>CA</th><th style={{textAlign:'right'}}>Volume</th><th style={{textAlign:'right'}}>Objectif</th><th style={{textAlign:'right'}}>Taux</th></tr>
                </thead>
                <tbody>
                  {comparaison.map((s: any) => (
                    <tr key={s.id}>
                      <td><strong>{s.nom}</strong></td>
                      <td>{s.region}</td>
                      <td style={{textAlign:'right'}}>{formatMontant(s.ca)} FCFA</td>
                      <td style={{textAlign:'right'}}>{formatMontant(s.volume)} L</td>
                      <td style={{textAlign:'right'}}>{formatMontant(s.objectif_ca)} FCFA</td>
                      <td style={{textAlign:'right'}}>
                        <span className={`badge badge-${s.taux_realisation >= 100 ? 'success' : s.taux_realisation >= 80 ? 'warning' : 'critical'}`}>
                          {s.taux_realisation}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* TOP / FLOP */}
          {tab === 'topflop' && (
            <div className="grid-2">
              <div className="card">
                <h3 style={{ color: '#78BE20' }}>Top 5 Stations</h3>
                <table className="table">
                  <thead><tr><th>#</th><th>Station</th><th>Region</th><th style={{textAlign:'right'}}>CA</th><th style={{textAlign:'right'}}>Volume</th></tr></thead>
                  <tbody>
                    {topFlop.top.map((s: any, i: number) => (
                      <tr key={s.id}>
                        <td><span className="badge badge-success">{i + 1}</span></td>
                        <td><strong>{s.nom}</strong></td>
                        <td>{s.region}</td>
                        <td style={{textAlign:'right'}}>{formatMontant(s.ca)} FCFA</td>
                        <td style={{textAlign:'right'}}>{formatMontant(s.volume)} L</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card">
                <h3 style={{ color: '#e53935' }}>Flop 5 Stations</h3>
                <table className="table">
                  <thead><tr><th>#</th><th>Station</th><th>Region</th><th style={{textAlign:'right'}}>CA</th><th style={{textAlign:'right'}}>Volume</th></tr></thead>
                  <tbody>
                    {topFlop.flop.map((s: any, i: number) => (
                      <tr key={s.id}>
                        <td><span className="badge badge-critical">{i + 1}</span></td>
                        <td><strong>{s.nom}</strong></td>
                        <td>{s.region}</td>
                        <td style={{textAlign:'right'}}>{formatMontant(s.ca)} FCFA</td>
                        <td style={{textAlign:'right'}}>{formatMontant(s.volume)} L</td>
                      </tr>
                    ))}
                    {topFlop.flop.length === 0 && <tr><td colSpan={5} style={{textAlign:'center'}}>Pas assez de donnees</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PRODUITS */}
          {tab === 'produits' && (
            <>
              <div style={{ marginBottom: 12, textAlign: 'right' }}>
                <button className="btn btn-sm" onClick={() => handleExport('produits')}>Exporter CSV</button>
              </div>
              <div className="grid-2">
                <div className="card">
                  <h3>Repartition par produit</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={produits} dataKey="ca" nameKey="produit" cx="50%" cy="50%" outerRadius={100} label>
                        {produits.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v: any) => formatMontant(v) + ' FCFA'} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <h3>Details</h3>
                  <table className="table">
                    <thead><tr><th>Produit</th><th style={{textAlign:'right'}}>Volume</th><th style={{textAlign:'right'}}>CA</th><th style={{textAlign:'right'}}>Part</th></tr></thead>
                    <tbody>
                      {(() => {
                        const totalCa = produits.reduce((s: number, p: any) => s + Number(p.ca), 0);
                        return produits.map((p: any, i: number) => (
                          <tr key={i}>
                            <td><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:COLORS[i%COLORS.length],marginRight:6}}></span><strong>{p.produit}</strong></td>
                            <td style={{textAlign:'right'}}>{formatMontant(p.volume)} L</td>
                            <td style={{textAlign:'right'}}>{formatMontant(p.ca)} FCFA</td>
                            <td style={{textAlign:'right'}}>{totalCa > 0 ? Math.round((p.ca / totalCa) * 100) : 0}%</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* EVOLUTION */}
          {tab === 'evolution' && (
            <div className="card">
              <h3>Evolution journaliere du CA - {MOIS_LABELS[mois - 1]} {annee}</h3>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={evolution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} />
                  <YAxis tickFormatter={(v: any) => `${Math.round(v / 1000000)}M`} />
                  <Tooltip formatter={(v: any) => formatMontant(v) + ' FCFA'} />
                  <Legend />
                  <Line type="monotone" dataKey="ca" name="CA" stroke="#ED0000" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="volume" name="Volume (L)" stroke="#004A97" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ECARTS */}
          {tab === 'ecarts' && (
            <>
              <div style={{ marginBottom: 12, textAlign: 'right' }}>
                <button className="btn btn-sm" onClick={() => handleExport('ecarts')}>Exporter CSV</button>
              </div>
              {ecarts.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 40 }}>Aucun ecart detecte pour cette periode</div>
              ) : (
                <>
                  <div className="stats-grid">
                    <div className="stat-card"><div className="stat-value">{ecarts.length}</div><div className="stat-label">Ecarts detectes</div></div>
                    <div className="stat-card" style={{borderTop:'4px solid #e53935'}}><div className="stat-value">{formatMontant(ecarts.reduce((s: number, e: any) => s + Math.abs(Number(e.ecart)), 0))}</div><div className="stat-label">Total ecarts (FCFA)</div></div>
                    <div className="stat-card" style={{borderTop:'4px solid #FF6B35'}}><div className="stat-value">{ecarts.filter((e: any) => Number(e.ecart) < 0).length}</div><div className="stat-label">Manquants</div></div>
                    <div className="stat-card" style={{borderTop:'4px solid #78BE20'}}><div className="stat-value">{ecarts.filter((e: any) => Number(e.ecart) > 0).length}</div><div className="stat-label">Excedents</div></div>
                  </div>
                  <table className="table">
                    <thead>
                      <tr><th>Station</th><th>Date</th><th>Produit</th><th>Pompiste</th><th style={{textAlign:'right'}}>Volume</th><th style={{textAlign:'right'}}>Montant</th><th style={{textAlign:'right'}}>Ecart</th></tr>
                    </thead>
                    <tbody>
                      {ecarts.map((e: any, i: number) => (
                        <tr key={i}>
                          <td><strong>{e.station_nom}</strong></td>
                          <td>{new Date(e.date_releve).toLocaleDateString('fr-FR')}</td>
                          <td>{e.produit}</td>
                          <td>{e.pompiste_nom}</td>
                          <td style={{textAlign:'right'}}>{formatMontant(e.volume_vendu)} L</td>
                          <td style={{textAlign:'right'}}>{formatMontant(e.montant_encaisse)} FCFA</td>
                          <td style={{textAlign:'right'}}>
                            <span style={{ color: Number(e.ecart) < 0 ? '#e53935' : '#78BE20', fontWeight: 600 }}>
                              {Number(e.ecart) > 0 ? '+' : ''}{formatMontant(e.ecart)} FCFA
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reporting;
